import type { Components } from "react-markdown"

/**
 * Normalize loose markdown bold/italic syntax (e.g. `** text **` → `**text**`)
 * so that react-markdown renders them correctly.
 */
export function normalizeMarkdown(text: string): string {
  let result = text
  // Strip spaces inside bold markers: "** Bold Text **" → "**Bold Text**"
  // [^*]+ ensures we don't cross into other bold pairs.
  result = result
    .replace(/\*\*[ \t]+([^*]+?)\*\*/g, "**$1**")
    .replace(/\*\*([^*]+?)[ \t]+\*\*/g, "**$1**")
  // Ensure numbered/bulleted list items have a space after the marker before bold
  // e.g. "1.**Language" → "1. **Language", "- **Item" stays "- **Item"
  result = result.replace(/^(\d+\.)\*\*/gm, "$1 **")
  // Convert single newlines to hard line breaks (two trailing spaces).
  // Replace blank lines (empty newlines) with a visible break using &nbsp;
  result = result
    .replace(/\n[ \t]*\n/g, "\n\n&nbsp;\n\n")
    .replace(/(?<!\n)\n(?!\n)/g, "  \n")
  return result
}

export const markdownComponents: Components = {
  a: ({ children, href, ...props }) => (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      style={{
        color: "hsl(var(--heroui-primary))",
        textDecoration: "underline",
        cursor: "pointer",
      }}
      onClick={(e) => {
        e.stopPropagation()
      }}
      {...props}
    >
      {children}
    </a>
  ),
  p: ({ children }) => <p style={{ margin: 0 }}>{children}</p>,
  h1: ({ children }) => (
    <h1 style={{ fontSize: "1rem", fontWeight: 600, margin: 0 }}>{children}</h1>
  ),
  h2: ({ children }) => (
    <h2 style={{ fontSize: "0.875rem", fontWeight: 600, margin: 0 }}>
      {children}
    </h2>
  ),
  h3: ({ children }) => (
    <h3 style={{ fontSize: "0.875rem", fontWeight: 500, margin: 0 }}>
      {children}
    </h3>
  ),
  ul: ({ children }) => (
    <ul style={{ margin: 0, paddingLeft: "1.25rem", listStyleType: "disc" }}>
      {children}
    </ul>
  ),
  ol: ({ children }) => (
    <ol
      style={{
        margin: 0,
        paddingLeft: "1.25rem",
        listStyleType: "decimal",
        lineHeight: 1.7,
      }}
    >
      {children}
    </ol>
  ),
  li: ({ children }) => (
    <li style={{ marginTop: "0.5rem", marginBottom: 0, display: "list-item" }}>
      {children}
    </li>
  ),
  code: ({ children }) => (
    <code
      style={{
        fontSize: "0.75rem",
        background: "hsl(var(--heroui-default-100))",
        padding: "0.125rem 0.25rem",
        borderRadius: "0.25rem",
      }}
    >
      {children}
    </code>
  ),
  pre: ({ children }) => (
    <pre style={{ fontSize: "0.75rem", margin: 0 }}>{children}</pre>
  ),
}
